import { useEffect, useState } from "react";

import apiClient from "../../../services/apiClient";
import "./PromotionPage.css";

// 학원 관리 > 프로모션(2026-09-30). 타겟 학교·교과서마다 무료 문법 자료 링크를 만든다.
// 링크마다 블로그용·쓰레드용 글(지역·학교·학원·출판사 포함)을 복사해 올리고, 링크로 들어온
// 분이 닉네임을 적어 신청하면 여기서 승인한다. 승인된 분은 같은 기기에서 바로 받는다.
// 링크 화면의 레벨테스트 안내는 지인에듀 신청서로 보낸다. 신청은 레벨테스트 탭 신규생에 뜬다.

const STATUS_LABEL = { pending: "승인 대기", approved: "승인", rejected: "거절" };

const formatTime = (value) =>
  new Date(value).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });

const copyText = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    window.prompt("아래 글을 복사해 주세요", text);
    return false;
  }
};

const PromotionPage = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ region: "", school_name: "", grade: "", textbook_code: "" });
  const [message, setMessage] = useState("");
  const [copied, setCopied] = useState("");
  const [openKey, setOpenKey] = useState("");
  const [showAll, setShowAll] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);

  const load = () =>
    apiClient.get("/api/v1/echo/teacher/promo", { withAuth: true }).then((response) => {
      setData(response.data);
      setForm((prev) => ({ ...prev, region: prev.region || response.data.default_region || "" }));
    });

  useEffect(() => {
    load();
  }, []);

  if (!data) return null;

  // 자료가 있는 교과서만 고를 수 있다. 없는 교과서는 팝업으로 지문을 보내 달라고 안내한다.
  const textbookList = (data.textbook_list || []).filter((book) => book.file_ready && (!form.grade || book.grade === form.grade));

  const create = async () => {
    setMessage("");
    if (!form.school_name.trim() || !form.textbook_code) {
      setMessage("학교 이름과 교과서를 골라 주세요.");
      return;
    }
    try {
      await apiClient.post(
        "/api/v1/echo/teacher/promo",
        { region: form.region, school_name: form.school_name, textbook_code: form.textbook_code },
        { withAuth: true }
      );
      setForm((prev) => ({ ...prev, school_name: "", textbook_code: "" }));
      setMessage("링크를 만들었어요. 아래에서 블로그·쓰레드 글을 복사해 올려 주세요.");
      load();
    } catch (caught) {
      if (caught.response?.data?.error === data.material_missing_message) setMissingOpen(true);
      setMessage(caught.response?.data?.error || "링크를 만들지 못했어요.");
    }
  };

  const copy = async (id, text) => {
    await copyText(text);
    setCopied(id);
    setTimeout(() => setCopied(""), 1500);
  };

  const decide = async (requestId, action) => {
    await apiClient.post("/api/v1/echo/teacher/promo/decide", { request_id: requestId, action }, { withAuth: true });
    load();
  };

  const toggleLink = async (key) => {
    await apiClient.post("/api/v1/echo/teacher/promo/toggle", { key }, { withAuth: true });
    load();
  };

  const requestList = data.request_list || [];
  const pendingList = requestList.filter((one) => one.status === "pending");
  const shownList = showAll ? requestList : pendingList;

  return (
    <div className="promo-page">
      <div className="section-title">프로모션</div>
      <p className="promo-page__lead">
        타겟 학교와 교과서를 고르면 <b>무료 문법 자료 링크</b>와 <b>블로그·쓰레드 글</b>이 만들어져요. 글에는 지역·학교·학원·출판사가 들어가
        검색에 잘 걸려요. 링크로 들어온 분이 신청하면 아래에서 <b>승인</b>해 주세요.
      </p>

      <section className="promo-page__card">
        <div className="promo-page__card-title">링크 만들기</div>
        <div className="promo-page__form">
          <label>
            <span>지역</span>
            <input value={form.region} placeholder="예: 대전 유성구" onChange={(event) => setForm({ ...form, region: event.target.value })} />
          </label>
          <label>
            <span>타겟 학교</span>
            <input value={form.school_name} placeholder="예: 어은중" onChange={(event) => setForm({ ...form, school_name: event.target.value })} />
          </label>
          <label>
            <span>학년</span>
            <select value={form.grade} onChange={(event) => setForm({ ...form, grade: event.target.value, textbook_code: "" })}>
              <option value="">전체</option>
              {(data.grade_list || []).map((grade) => (
                <option key={grade} value={grade}>{grade}</option>
              ))}
            </select>
          </label>
          <label>
            <span>교과서(출판사)</span>
            <select value={form.textbook_code} onChange={(event) => setForm({ ...form, textbook_code: event.target.value })}>
              <option value="">교과서를 고르세요</option>
              {textbookList.map((book) => (
                <option key={book.code} value={book.code}>
                  {book.grade} {book.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button type="button" className="promo-page__toggle promo-page__missing-open" onClick={() => setMissingOpen(true)}>
          찾는 교과서가 없나요?
        </button>
        <button type="button" className="promo-page__primary" onClick={create}>링크 만들기</button>
        {message && <p className="promo-page__message">{message}</p>}
      </section>

      {missingOpen && (
        <div className="promo-page__popup" role="dialog" aria-modal="true" onClick={() => setMissingOpen(false)}>
          <div className="promo-page__popup-card" onClick={(event) => event.stopPropagation()}>
            <b>자료가 아직 없어요!</b>
            <p>
              기말고사 범위 지문을
              <br />
              <strong>유현쌤 카톡</strong>으로 빨리 보내 주시면
              <br />
              바로 작업해 드릴게요!
            </p>
            <button type="button" className="promo-page__primary" onClick={() => setMissingOpen(false)}>확인</button>
          </div>
        </div>
      )}

      <section className="promo-page__card">
        <div className="promo-page__card-title">내 링크 ({(data.link_list || []).length})</div>
        {(data.link_list || []).length === 0 && <p className="promo-page__help">아직 만든 링크가 없어요.</p>}
        {(data.link_list || []).map((link) => (
          <div key={link.key} className={`promo-page__link${link.is_active ? "" : " promo-page__link--off"}`}>
            <div className="promo-page__link-head">
              <div className="promo-page__link-title">
                <b>{link.school_name} {link.textbook_label}</b>
                <span>{link.region || "지역 없음"} · {link.url}</span>
              </div>
              <div className="promo-page__badges">
                {!link.file_ready && (
                  <button type="button" className="promo-page__badge promo-page__badge--wait" onClick={() => setMissingOpen(true)}>
                    자료 준비 중
                  </button>
                )}
                {link.pending_count > 0 && <em className="promo-page__badge promo-page__badge--wait">대기 {link.pending_count}</em>}
                {link.approved_count > 0 && <em className="promo-page__badge promo-page__badge--ok">승인 {link.approved_count}</em>}
              </div>
            </div>
            <div className="promo-page__actions">
              <button type="button" onClick={() => copy(`${link.key}-blog`, link.blog_text)}>
                {copied === `${link.key}-blog` ? "복사했어요" : "블로그 글 복사"}
              </button>
              <button type="button" onClick={() => copy(`${link.key}-threads`, link.threads_text)}>
                {copied === `${link.key}-threads` ? "복사했어요" : "쓰레드 글 복사"}
              </button>
              <a className="promo-page__image-link" href={link.card_portrait_url} download target="_blank" rel="noreferrer">
                세로 이미지 저장
              </a>
              <a className="promo-page__image-link" href={link.card_square_url} download target="_blank" rel="noreferrer">
                정사각 이미지 저장
              </a>
              <button type="button" onClick={() => copy(`${link.key}-url`, link.url)}>
                {copied === `${link.key}-url` ? "복사했어요" : "링크만 복사"}
              </button>
              <button type="button" className="promo-page__ghost" onClick={() => setOpenKey(openKey === link.key ? "" : link.key)}>
                {openKey === link.key ? "글 접기" : "글 미리보기"}
              </button>
              <button type="button" className="promo-page__ghost" onClick={() => toggleLink(link.key)}>
                {link.is_active ? "링크 끄기" : "다시 켜기"}
              </button>
            </div>
            {openKey === link.key && (
              <div className="promo-page__preview">
                <div>
                  <b>블로그</b>
                  <pre>{link.blog_text}</pre>
                </div>
                <div>
                  <b>쓰레드</b>
                  <pre>{link.threads_text}</pre>
                </div>
                <div className="promo-page__cards">
                  <b>함께 올릴 이미지</b>
                  <div>
                    <img src={link.card_portrait_url} alt="세로 이미지" loading="lazy" />
                    <img src={link.card_square_url} alt="정사각 이미지" loading="lazy" />
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </section>

      <section className="promo-page__card">
        <div className="promo-page__card-head">
          <div className="promo-page__card-title">자료 신청 {pendingList.length > 0 ? `· 승인 대기 ${pendingList.length}명` : ""}</div>
          <button type="button" className="promo-page__toggle" onClick={() => setShowAll((prev) => !prev)}>
            {showAll ? "승인 대기만 보기" : `전체 보기 (${requestList.length})`}
          </button>
        </div>
        <p className="promo-page__help">승인하면 그분이 신청한 기기(같은 브라우저나 같은 인터넷)에서 바로 받을 수 있어요.</p>
        {shownList.length === 0 ? (
          <p className="promo-page__help">{showAll ? "아직 신청이 없어요." : "승인을 기다리는 신청이 없어요."}</p>
        ) : (
          shownList.map((one) => (
            <div key={one.id} className="promo-page__row">
              <div className="promo-page__who">
                <b>{one.nickname}</b>
                <span>
                  {one.title} · {formatTime(one.created_at)}
                  {one.download_count > 0 ? ` · ${one.download_count}번 받음` : ""}
                </span>
              </div>
              <span className={`promo-page__status promo-page__status--${one.status}`}>{STATUS_LABEL[one.status]}</span>
              <div className="promo-page__row-actions">
                {one.status !== "approved" && (
                  <button type="button" className="promo-page__approve" onClick={() => decide(one.id, "approve")}>승인</button>
                )}
                {one.status !== "rejected" && (
                  <button type="button" className="promo-page__reject" onClick={() => decide(one.id, "reject")}>거절</button>
                )}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
};

export default PromotionPage;
